<template>
  <BaseLayout title="Evaluations">
    <!-- Main Content (Center)-->
    <div class="container my-4">
      <h1 v-if="isTeacher && evaluationStats" class="display-4 text-center">Evaluations overview</h1>
      <h1 v-else-if="isTeacher" class="display-4 text-center">Student evaluations</h1>
      <h1 v-else class="display-4 text-center">Evaluations</h1>
      <!-- Loading -->
      <div v-if="loading" class="text-center my-4">
        <div class="spinner-border" role="status">
          <span class="sr-only">&nbsp;&nbsp;Loading...</span>
        </div>
      </div>
      <div v-else>
        <div v-if="isTeacher && evaluationStats" class="d-flex justify-content-end gap-1 mb-2">
          <button class="btn btn-outline-secondary btn-sm" :disabled="prevDeadlineId === null" @click="navigateToDeadline(prevDeadlineId)" title="Previous evaluation">
            <i class="bi bi-arrow-left"></i>
          </button>
          <button class="btn btn-outline-secondary btn-sm" :disabled="nextDeadlineId === null" @click="navigateToDeadline(nextDeadlineId)" title="Next evaluation">
            <i class="bi bi-arrow-right"></i>
          </button>
        </div>
        <div v-if="isTeacher && evaluationStats && subtaskSummary" class="card mb-4">
          <!-- F8: tab switcher -->
          <div class="card-header p-0">
            <ul class="nav nav-tabs card-header-tabs">
              <li class="nav-item">
                <button :class="['nav-link', activeTab === 'stats' ? 'active' : '']" @click="switchToStats">
                  <i class="bi bi-bar-chart me-1"></i>Statistics
                </button>
              </li>
              <li class="nav-item">
                <button :class="['nav-link', activeTab === 'overview' ? 'active' : '']" @click="switchToOverview">
                  <i class="bi bi-people me-1"></i>Student Overview
                </button>
              </li>
            </ul>
          </div>
          <div class="card-body">
            <!-- ── Statistics tab ── -->
            <!-- Evaluation Stats Section -->
            <div v-if="activeTab === 'stats' && evaluationStats" class="mb-4">
              <div class="row mb-4">
                <div class="col-md-4 mb-2">
                  <div class="card">
                  <div class="card-body">
                    <p class="card-text"><strong>Homework:</strong> {{ evaluationStats.homework_title }}</p>
                  </div>
                  </div>
                </div>
                <div class="col-md-4 mb-2">
                  <div class="card">
                  <div class="card-body">
                    <p class="card-text"><strong>Deadline:</strong> {{ formatDate(evaluationStats.deadline_date) }}</p>
                  </div>
                  </div>
                </div>
                <div class="col-md-4 mb-2">
                  <div class="card">
                  <div class="card-body">
                    <p class="card-text"><strong>Deadline Type:</strong> {{ getEvaluationTypeLabel(evaluationStats.deadline_evaluation_type) }}</p>
                  </div>
                  </div>
                </div>
              </div>
              <div class="alert alert-info mt-3" role="alert"
                v-if="evaluationStats.run_canceled">
                <p class="card-text">Some evaluations from this run were canceled. Overview count only finished evaluations.</p>
              </div>
              <h5>Evaluation stats</h5>
                <ul class="list-group">
                <li class="list-group-item d-flex align-items-center">
                  <i class="bi bi-people-fill text-primary me-2"></i>
                  Total students:  {{ evaluationStats.total_students }}
                </li>
                <li class="list-group-item d-flex align-items-center">
                  <i class="bi bi-check-square text-success me-2"></i>
                  Evaluations ended:  {{ evaluationStats.finished_evaluations }}
                </li>
                <li class="list-group-item d-flex align-items-center">
                  <i class="bi bi-hourglass-split text-warning me-2"></i>
                  Evaluations not finished: {{ evaluationStats.not_finished_evaluations }}
                </li>
                </ul>
            </div>
            <!-- Subtask Summary Section -->
            <div v-if="activeTab === 'stats' && subtaskSummary">
              <h5>Subtask Summary</h5>
              <div class="row">
                <div v-for="(data, subtaskId) in groupedSubtasks" :key="subtaskId" class="col-md-6 mb-3">
                  <div class="card">
                    <div class="card-header">
                      {{ data.subtask_title }}
                    </div>
                    <div v-if="!data.to_evaluate" class="card-body">
                      <p class="card-text">
                        <i class="bi bi-info-circle-fill me-2"></i>
                        Task not to be evaluated.
                      </p>
                    </div>
                    <div v-else>
                      <ul class="list-group list-group-flush">
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                          <span>
                            <i class="bi bi-check-circle-fill text-success me-2"></i>Success: {{ data.success }}
                          </span>
                            <button v-if="data.success > 0" class="btn btn-primary btn-sm" @click="openSpecificStudentList('success', subtaskId)">
                            <i class="bi bi-search me-1"></i> View students
                            </button>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                          <span>
                            <i class="bi bi-x-circle-fill text-danger me-2"></i>Fail: {{ data.fail }}
                          </span>
                          <button v-if="data.fail > 0" class="btn btn-primary btn-sm" @click="openSpecificStudentList('fail', subtaskId)">
                            <i class="bi bi-search me-1"></i> View students
                          </button>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                          <span>
                            <i class="bi bi-file-earmark-x text-info me-2"></i>No Submission: {{ data.missing }}
                          </span>
                          <button v-if="data.missing > 0" class="btn btn-primary btn-sm" @click="openSpecificStudentList('missing', subtaskId)">
                            <i class="bi bi-search me-1"></i> View students
                          </button>
                        </li>
                        <li class="list-group-item d-flex justify-content-between align-items-center">
                          <span>
                            <i class="bi bi-exclamation-triangle-fill text-warning me-2"></i>Internal Error: {{ data.container_error }}
                          </span>
                          <button v-if="data.container_error > 0" class="btn btn-primary btn-sm" @click="openSpecificStudentList('container_error', subtaskId)">
                            <i class="bi bi-search me-1"></i> View students
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Student Overview tab (F8) ── -->
            <div v-if="activeTab === 'overview'">
              <div v-if="overviewLoading" class="text-center my-4">
                <div class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>
              <div v-else-if="perStudentItems.length === 0" class="alert alert-info mb-0">No students found for this semester.</div>
              <div v-else class="table-responsive">
                <table class="table table-hover align-middle mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>UCO</th>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Passed</th>
                      <th>Duration</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in perStudentItems" :key="item.student_id">
                      <td>{{ item.uco }}</td>
                      <td>{{ item.name || '—' }}</td>
                      <td>
                        <span :class="['badge', getStudentStatusBadge(item).cls]">
                          {{ getStudentStatusBadge(item).label }}
                        </span>
                      </td>
                      <td>
                        <i v-if="item.passed === true" class="bi bi-check-circle-fill text-success"></i>
                        <i v-else-if="item.passed === false" class="bi bi-x-circle-fill text-danger"></i>
                        <span v-else class="text-muted">—</span>
                      </td>
                      <td>{{ formatDuration(item.duration_seconds) }}</td>
                      <td class="text-end">
                        <router-link v-if="item.evaluation_id" :to="`evaluation/${item.evaluation_id}`" class="btn btn-sm btn-outline-secondary me-1">
                          <i class="bi bi-eye"></i>
                        </router-link>
                        <button class="btn btn-sm btn-outline-primary" @click="openPassFail(item)">
                          <i class="bi bi-pencil-square me-1"></i>Pass / Fail
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="evaluations.length === 0" class="alert alert-info text-center">
          No evaluations available.
          <br>
          <button @click="fetchAgainEvaluations()" class="btn btn-primary">
            <i class="bi bi-arrow-clockwise me-1"></i> Refresh
          </button>
        </div>
        <div v-else>
          <div v-for="(group, homeworkId) in evaluationsByHomework" :key="homeworkId" class="mb-4">
            <h2 class="mb-3">{{ group.homework.title }}</h2>
            <!-- Display each evaluation as a line in a list -->
            <ul class="list-group">
              <li v-for="evaluation in group.evaluations" :key="evaluation.id" class="list-group-item d-flex justify-content-between align-items-center">
                <div>
                  <span v-if="evaluation.container_error == true && !evaluation.in_queue" class="badge text-bg-danger me-3">Internal error</span>
                  <span v-if="evaluation.is_manual" class="badge text-bg-info me-3">Manually evaluated</span>
                  <!-- F3: active run status badge -->
                  <span v-if="evaluation.in_queue && !evaluation.run_started_at" class="badge text-bg-warning text-dark me-3">
                    <span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>Queued
                  </span>
                  <span v-else-if="evaluation.in_queue && evaluation.run_started_at" class="badge text-bg-warning text-dark me-3">
                    <span class="spinner-border spinner-border-sm me-1" aria-hidden="true"></span>Running
                  </span>
                  <span class="me-3"><strong>Deadline:</strong> {{ formatDate(evaluation.deadline.date) }}</span>
                  <span class="me-3">
                    <strong>Result:&nbsp;</strong>
                    <!-- F3: show previous result dimmed when a new run is active -->
                    <span v-if="evaluation.in_queue && evaluation.subevaluations_passed != null" class="text-muted">
                      <i :class="evaluation.subevaluations_passed === true ? 'bi bi-check-circle-fill' : 'bi bi-x-circle-fill'"></i>
                      <small class="ms-1">(previous run)</small>
                    </span>
                    <i v-else-if="!evaluation.in_queue && evaluation.subevaluations_passed === true" class="bi bi-check-circle-fill text-success"></i>
                    <i v-else-if="!evaluation.in_queue && evaluation.subevaluations_passed === false" class="bi bi-x-circle-fill text-danger"></i>
                    <i v-else-if="evaluation.in_queue" class="bi bi-hourglass-split text-warning"></i>
                    <span v-else class="text-muted">—</span>
                  </span>
                  <span class="me-3"><strong>Type:</strong> {{ getEvaluationTypeLabel(evaluation.deadline.evaluation_type) }}</span>
                </div>
                <router-link :to="`evaluation/${evaluation.id}`" class="btn btn-primary btn-sm">
                  <i class="bi bi-eye"></i> View
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Sidebar Slot -->
    <template v-slot:right-sidebar>
      <div v-if="isTeacher" class="card p-3">
        <h5 class="card-title">Quick Actions:</h5>
        <button class="btn btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#chooseHomeworkDeadlineModalForm">
          <i class="bi bi-calendar-plus me-1"></i> Search for Evaluation
        </button>
        <button class="btn btn-primary mb-2" data-bs-toggle="modal" data-bs-target="#chooseStudentByUcoModalForm">
          <i class="bi bi-search me-1"></i> Search for Student
        </button>
        <button class="btn btn-success mb-2" data-bs-toggle="modal" data-bs-target="#evaluationStartModalForm">
          <i class="bi bi-play-circle me-1"></i> Start evaluation
        </button>
        <button class="btn btn-danger" data-bs-toggle="modal" data-bs-target="#chooseHomeworkDeadlineToDeleteRunModalForm">
          <i class="bi bi-trash me-1"></i> Delete evaluation run
        </button>
      </div>
    </template>

    <!-- Modal for select student -->
    <ChooseStudentByUcoModalForm v-if="isTeacher"
      title="Search for student"
      ref="ChooseStudentByUcoModalForm"
      @submitForm="searchStudent"
    />

    <!-- Modal for start evaluation -->
    <EvaluationStartModalForm v-if="isTeacher"
      ref="EvaluationStartModalForm"
    />

    <!-- Modal for choose homework deadline -->
    <ChooseHomeworkDeadlineModalForm v-if="isTeacher"
      ref="ChooseHomeworkDeadlineModalForm"
      @submitForm="fetchEvaluationStatsAndSubtaskSummary"
    />

    <!-- Modal for choose homework deadline -->
    <ChooseHomeworkDeadlineToDeleteRunModalForm v-if="isTeacher"
      ref="ChooseHomeworkDeadlineToDeleteRunModalForm"
      @submitForm="deleteEvaluationRun"
    />

    <!-- Modal for specific student list -->
    <SpecificStudentList 
      v-if="isTeacher" 
      :specifiedData="specifiedData" 
      ref="SpecificStudentListModal" 
      :onRedirectEvaluation="handleRedirectEvaluation" 
    />

    <!-- Modal for manual pass/fail (F8) -->
    <PassFailModal
      v-if="isTeacher"
      :student="selectedStudentForModal"
      :subtasks="perStudentSubtasks"
      :deadline-id="latestDeadlineId"
      :manual-results="selectedStudentManualResults"
      @saved="onManualSaved"
    />

    <StemErrorModal ref="stemErrorModal" />
  </BaseLayout>
</template>

<script>
import { Modal } from 'bootstrap';
import BaseLayout from '@/components/BaseLayout.vue';
import EvaluationController from '@/controllers/EvaluationController';
import StudentController from '@/controllers/StudentController';
import EvaluationStartModalForm from '@/components/EvaluationStartModalForm.vue';
import ChooseStudentByUcoModalForm from '@/components/ChooseStudentByUcoModalForm.vue';
import ChooseHomeworkDeadlineToDeleteRunModalForm from '@/components/ChooseHomeworkDeadlineToDeleteRunModalForm.vue';
import StemErrorModal from '@/components/StemErrorModal.vue';
import DeadlineController from '@/controllers/DeadlineController';
import ChooseHomeworkDeadlineModalForm from '@/components/ChooseHomeworkDeadlineModalForm.vue';
import Deadline from '@/models/deadline';
import SpecificStudentList from '@/components/SpecificStudentListModal.vue';
import PassFailModal from '@/components/PassFailModal.vue';
import { mapGetters } from 'vuex';

export default {
  name: 'EvaluationView',
  components: {
    BaseLayout,
    EvaluationStartModalForm,
    ChooseStudentByUcoModalForm,
    ChooseHomeworkDeadlineModalForm,
    ChooseHomeworkDeadlineToDeleteRunModalForm,
    SpecificStudentList,
    PassFailModal,
    StemErrorModal
  },
  data() {
    return {
      evaluations: [],
      evaluationStats: null, // JSON
      subtaskSummary: null,  // JSON
      loading: true,
      studentId: '',
      showModalSelectStudent: false,
      showModalStartEvaluation: false,
      specifiedData: null,
      latestDeadlineId: null,
      allDeadlines: [], // sorted by date ascending for prev/next navigation
      // F8: Student Overview tab
      activeTab: 'stats',
      perStudentItems: [],
      overviewLoading: false,
      selectedStudentForModal: null,
      selectedStudentManualResults: {},
      // F3: live polling for student view
      studentPollTimeoutId: null,
      pollAlive: true,
      studentPollStart: null,
    };
  },
  computed: {
    ...mapGetters(['isTeacher', 'userId', 'lastEvaluationDeadlineId']),
    currentDeadlineIndex() {
      return this.allDeadlines.findIndex(d => d.id === this.latestDeadlineId);
    },
    prevDeadlineId() {
      const idx = this.currentDeadlineIndex;
      return idx > 0 ? this.allDeadlines[idx - 1].id : null;
    },
    nextDeadlineId() {
      const idx = this.currentDeadlineIndex;
      return idx !== -1 && idx < this.allDeadlines.length - 1 ? this.allDeadlines[idx + 1].id : null;
    },

    evaluationsByHomework() {
      const grouped = {};

      this.evaluations.forEach(evaluation => {
        const homework = evaluation.deadline.homework;
        if (!grouped[homework.id]) {
          grouped[homework.id] = {
            homework,
            evaluations: []
          };
        }
        grouped[homework.id].evaluations.push(evaluation);
      });

      // Sort evaluations for each homework from newest to oldest by deadline date.
      Object.values(grouped).forEach(group => {
        group.evaluations.sort((a, b) => new Date(b.deadline.date) - new Date(a.deadline.date));
      });

      return grouped;
    },
    groupedSubtasks() {
      const grouped = {};
      if (!this.subtaskSummary) return grouped;
      this.subtaskSummary.forEach(item => {
        if (!grouped[item.subtask_id]) {
          grouped[item.subtask_id] = {
            subtask_title: item.subtask_title,
            success: 0,
            fail: 0,
            missing: 0,
            container_error: 0,
            to_evaluate: item.to_evaluate
          };
        }
        // Set the outcome counts
        grouped[item.subtask_id][item.outcome] = item.count;
      });

      return grouped;
    },
    // F8: subtask list for PassFailModal, derived from subtask summary
    perStudentSubtasks() {
      return Object.entries(this.groupedSubtasks)
        .filter(([, d]) => d.to_evaluate)
        .map(([id, d]) => ({ id: parseInt(id), title: d.subtask_title }));
    },
  },
  watch: {
    '$route.query'(newQuery) {
      // Sync tab from URL
      const tab = newQuery.tab === 'overview' ? 'overview' : 'stats';
      if (tab !== this.activeTab) {
        this.activeTab = tab;
        if (tab === 'overview' && this.latestDeadlineId && this.perStudentItems.length === 0) {
          this.loadPerStudentOverview(this.latestDeadlineId);
        }
      }
      // Reload stats when deadline changes
      const id = parseInt(newQuery.deadline_id);
      if (!isNaN(id) && (id !== this.latestDeadlineId || !this.evaluationStats)) {
        this.loadEvaluationStats(id);
      } else if (newQuery.deadline_id && isNaN(id)) {
        this.$router.replace({ query: {} });
      }
    }
  },
  beforeUnmount() {
    this.pollAlive = false;
    clearTimeout(this.studentPollTimeoutId);
    this.studentPollTimeoutId = null;
  },
  async created() {
    try {
      if (!this.isTeacher) {
        const studentId = this.userId;
        this.evaluations = await EvaluationController.fetchEvaluationsWithDeadlineAndHomework(studentId);
        this.studentPollStart = Date.now();
        this._scheduleStudentPoll();
      } else {
        // Restore tab from URL before loading stats so loadEvaluationStats can pre-fetch overview
        if (this.$route.query.tab === 'overview') {
          this.activeTab = 'overview';
        }

        // Load all deadlines for prev/next navigation
        const deadlines = await DeadlineController.fetchDeadlines();
        this.allDeadlines = deadlines.sort((a, b) => new Date(a.date) - new Date(b.date));

        const urlDeadlineId = parseInt(this.$route.query.deadline_id);
        if (!isNaN(urlDeadlineId)) {
          // URL param takes priority (refresh or shared link)
          await this.loadEvaluationStats(urlDeadlineId);
        } else if (this.$route.query.deadline_id) {
          // Neplatná hodnota v URL — presmeruj na čistú URL a načítaj posledný deadline
          this.$router.replace({ query: {} });
          await this._loadFallbackDeadline();
        } else {
          // No URL param — restore last opened from store, else load latest
          const stored = this.lastEvaluationDeadlineId;
          if (stored) {
            await this.loadEvaluationStats(stored);
          } else {
            await this._loadFallbackDeadline();
          }
        }
      }
    } catch (error) {
      this.$refs.stemErrorModal.show('Error', error.message);
    } finally {
      this.loading = false;
    }
  },
  methods: {
    async fetchAgainEvaluations(studentId = null) {
      this.loading = true;
      try {
        // fetch evaluations for specific student, allow only by teacher
        if (this.isTeacher && studentId) {
          this.evaluations = await EvaluationController.fetchEvaluationsWithDeadlineAndHomework(studentId);
        }
        // fetch for current user (evaluations are only for students)
        else if (!this.isTeacher) {
          studentId = this.userId;
          this.evaluations = await EvaluationController.fetchEvaluationsWithDeadlineAndHomework(studentId);
          this.studentPollStart = Date.now();
          this._scheduleStudentPoll();
        }
        // For teachers without studentId - reload the overview page (similar to when load page)
        else if (this.isTeacher && !studentId) {
          this.evaluationStats = null;
          this.subtaskSummary = null;
          this.evaluations = [];
          
          const deadline = await DeadlineController.fetchLatestDeadline();
          if (deadline) {
            await this.loadEvaluationStats(deadline.id);
          } else {
            this.latestDeadlineId = null;
            this.perStudentItems = [];
          }
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      let result = '';
      if (date){
        result = Deadline.formatDateTime(date);
      }
      return result;
    },
    getEvaluationTypeLabel(type) {
      return Deadline.getEvaluationTypeLabel(type);
    },
    async searchStudent(studentUco) {
      try {
        this.loading = true;
        this.evaluationStats = null;
        this.subtaskSummary = null;
        const student = await StudentController.fetchStudentByUCO(studentUco);
        await this.fetchAgainEvaluations(student.id);
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async fetchEvaluationStatsAndSubtaskSummary(deadlineId) {
      this.$router.push({ query: { ...this.$route.query, deadline_id: deadlineId } });
    },
    async loadEvaluationStats(deadlineId) {
      try {
        this.loading = true;
        this.latestDeadlineId = deadlineId;
        this.perStudentItems = []; // reset overview when deadline changes
        this.$store.commit('setLastEvaluationDeadlineId', deadlineId);
        this.evaluationStats = await EvaluationController.fetchEvaluationStats(deadlineId);
        this.subtaskSummary = await EvaluationController.fetchSubtaskSummary(deadlineId);
        if (this.activeTab === 'overview') {
          await this.loadPerStudentOverview(deadlineId);
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    async _loadFallbackDeadline() {
      const deadline = await DeadlineController.fetchLatestDeadline();
      if (deadline) await this.loadEvaluationStats(deadline.id);
    },
    navigateToDeadline(deadlineId) {
      if (deadlineId !== null) {
        this.$router.push({ query: { ...this.$route.query, deadline_id: deadlineId } });
      }
    },
    async deleteEvaluationRun(deadlineId, deleteDeadline = false) {
      try {
        // Compormation before deletion
        var message = 'Do you want to delete this evaluation run? This will permanently remove all evaluations and their subevaluations.';
        if (deleteDeadline) {
          message = 'Do you want to delete this evaluation run and deadline? This will permanently remove all evaluations, their subevaluations and deadline.';
        }

        const confirmation = confirm(message);
        if (!confirmation) return;

        this.loading = true;
        await EvaluationController.deleteEvaluationRun(deadlineId, deleteDeadline);
        this.$store.dispatch('triggerAlert', {
          title: 'Success',
          message: 'Evaluation run deleted successfully.',
          type: 'success',
        });
        this.evaluationStats = null;
        this.subtaskSummary = null;
        this.evaluations = [];

        // Fetch the latest deadline and update the evaluation stats and subtask summary
        const deadline = await DeadlineController.fetchLatestDeadline();
        if (deadline) {
          await this.loadEvaluationStats(deadline.id);
        } else {
          this.latestDeadlineId = null;
          this.perStudentItems = [];
        }
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    openSpecificStudentList(resultType, subtaskId) {
      this.specifiedData = {
        subevaluation_result_type: resultType,
        deadline_id: this.latestDeadlineId,
        subtask_id: subtaskId
      };

      const modalEl = document.getElementById('specificStudentListModal');
      const modal = new Modal(modalEl);
      modal.show();
    },
    handleRedirectEvaluation(evaluationId) {
      this.$router.push(`evaluation/${evaluationId}`);
    },

    // F8: Student Overview tab methods
    switchToStats() {
      this.activeTab = 'stats';
      const query = { ...this.$route.query };
      delete query.tab;
      this.$router.push({ query });
    },
    async switchToOverview() {
      this.activeTab = 'overview';
      this.$router.push({ query: { ...this.$route.query, tab: 'overview' } });
      if (this.latestDeadlineId && this.perStudentItems.length === 0) {
        await this.loadPerStudentOverview(this.latestDeadlineId);
      }
    },
    async loadPerStudentOverview(deadlineId) {
      this.overviewLoading = true;
      try {
        const data = await EvaluationController.fetchPerStudentOverview(deadlineId);
        this.perStudentItems = data.items || [];
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.overviewLoading = false;
      }
    },
    getStudentStatusBadge(item) {
      if (!item.evaluation_id) return { label: 'Not evaluated', cls: 'bg-secondary' };
      if (item.in_queue && !item.run_started_at) return { label: 'Queued', cls: 'bg-warning text-dark' };
      if (item.in_queue && item.run_started_at) return { label: 'Running', cls: 'bg-warning text-dark' };
      if (item.container_error) return { label: 'Error', cls: 'bg-danger' };
      if (item.canceled) return { label: 'Canceled', cls: 'bg-secondary' };
      if (item.is_manual) return { label: 'Manual', cls: 'bg-info text-dark' };
      return { label: 'Evaluated', cls: 'bg-success' };
    },
    // F3: poll student evaluations every 5s when any run is active ----------
    _hasActiveEvaluation() {
      return this.evaluations.some(e => e.in_queue);
    },
    _scheduleStudentPoll() {
      if (this.studentPollTimeoutId) clearTimeout(this.studentPollTimeoutId);
      if (!this.pollAlive) return;
      if (!this._hasActiveEvaluation()) return;
      // Stop polling after 30 minutes to avoid endless loops for stuck evaluations.
      if (this.studentPollStart && Date.now() - this.studentPollStart > 30 * 60 * 1000) return;
      this.studentPollTimeoutId = setTimeout(() => this._studentPollTick(), 5000);
    },
    async _studentPollTick() {
      this.studentPollTimeoutId = null;
      if (document.hidden) {
        // Page not visible — reschedule without fetching
        this._scheduleStudentPoll();
        return;
      }
      try {
        const fresh = await EvaluationController.fetchEvaluationsWithDeadlineAndHomework(this.userId);
        if (!this.pollAlive) return;
        this.evaluations = fresh;
      } catch (e) {
        const status = e?.response?.status;
        if (status >= 400 && status < 500 && status !== 408 && status !== 429) return;
        // transient error — reschedule
      }
      this._scheduleStudentPoll();
    },
    formatDuration(seconds) {
      if (seconds == null) return '—';
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return m > 0 ? `${m}m ${s}s` : `${s}s`;
    },
    openPassFail(item) {
      this.selectedStudentForModal = { uco: item.uco, name: item.name, id: item.student_id };
      this.selectedStudentManualResults = item.manual_subtask_results || {};
      this.$nextTick(() => {
        const el = document.getElementById('passFailModal');
        if (el) new Modal(el).show();
      });
    },
    async onManualSaved() {
      this.$store.dispatch('triggerAlert', {
        type: 'success',
        title: 'Saved',
        message: 'Manual result saved successfully.',
      });
      if (this.latestDeadlineId) {
        this.evaluationStats = await EvaluationController.fetchEvaluationStats(this.latestDeadlineId);
        this.subtaskSummary = await EvaluationController.fetchSubtaskSummary(this.latestDeadlineId);
        await this.loadPerStudentOverview(this.latestDeadlineId);
      }
    },
  }
}
</script>